import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { isUserAuthorized } from '../../../../util/userHelpers';
import { TOPBAR_AVATAR_VARIANTS } from '../../../../ducks/user.duck';
import {
  Avatar,
  InlineTextButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
} from '../../../../components';
import { ViewProfileLink } from '../../../ProfileSettingsPage/ProfileSettingsPage';

import css from './TopbarProfileMenu.module.css';

/**
 * Profile dropdown menu for topbar (desktop and mobile).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className - Additional class for the root Menu wrapper
 * @param {string?} props.currentPage
 * @param {Object} props.currentUser
 * @param {Function} props.onLogout
 * @param {boolean} props.showManageListingsLink
 * @param {Object} props.intl
 * @returns {JSX.Element}
 */
const TopbarProfileMenu = props => {
  const {
    className,
    currentPage,
    currentUser,
    onLogout,
    showManageListingsLink,
    intl,
    isMobile = false,
    isModalHeader = false,
    menuLabelId = 'profile-menu-label',
  } = props;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);

  return (
    <Menu
      skipFocusOnNavigation={true}
      className={classNames(
        css.root,
        isMobile && css.mobileRoot,
        isModalHeader && css.modalHeaderRoot,
        className
      )}
    >
      <MenuLabel
        id={menuLabelId}
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar
          className={css.avatar}
          user={currentUser}
          imageVariants={TOPBAR_AVATAR_VARIANTS}
          disableProfileLink
        />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfilePage">
          <ViewProfileLink
            userUUID={currentUser?.id?.uuid}
            isUnauthorizedUser={isUnauthorizedUser}
            className={classNames(css.menuLink, currentPageClass('ProfilePage'))}
            id="TopbarDesktop.profilePageLink"
          />
        </MenuItem>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default TopbarProfileMenu;
